import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowClockwise, Plus, Tray } from '@phosphor-icons/react';
import { ImmoButton, Sheet } from '../../../../components/immobilien/ui';
import { EmailOrdnerNav, useLaufendeSerienmails, useSerienmailErlaubt } from '../../../../components/immobilien/sidebar/EmailModus';
import { objektOrdnerLabel, ordnerLabel, syncText } from '../../../../components/immobilien/sidebar/emailOrdner';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import { useEmailNav } from '../EmailNavContext';
import EmailListe from '../EmailListe';
import SerienmailListe from '../../serienmail/SerienmailListe';
import SerienmailAssistent from '../../serienmail/SerienmailAssistent';
import SerienmailDetail from '../../serienmail/SerienmailDetail';

type Profil = { role?: string | null; allowed_modules?: unknown } & Record<string, unknown>;

/**
 * E-Mail-Bereich im neuen Design (Handoff E-Mails §6): Kopf, Ordner in der Sidebar bzw. mobil im Sheet,
 * Serienmail als Ordner (?ordner=serienmail&lauf=neu|<id>). Die Fachlogik bleibt in EmailListe und lib/emails.
 */
export function EmailBereich({ userProfile, initialEmailId, onClearInitial }: {
  userProfile?: Profil; initialEmailId?: string | null; onClearInitial?: () => void;
}) {
  const [params] = useSearchParams();
  const { nav } = useEmailNav();
  const { mobil } = useMobilModus();
  const serienmailErlaubt = useSerienmailErlaubt(userProfile?.role ?? null, userProfile?.allowed_modules);
  const laufend = useLaufendeSerienmails(serienmailErlaubt && mobil, nav?.activeTab);
  const [sheet, setSheet] = useState(false);
  const [jetzt, setJetzt] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setJetzt(new Date()), 60000); return () => clearInterval(t); }, []);

  // Ohne freigeschaltetes Modul gibt es keinen Ordner „Serienmail“ – auch nicht per URL.
  useEffect(() => {
    if (nav?.activeTab === 'serienmail' && !serienmailErlaubt) nav.onSelectTab('posteingang');
  }, [nav, serienmailErlaubt]);

  const lauf = params.get('lauf');
  const serienmail = !serienmailErlaubt ? null
    : lauf === 'neu' ? <SerienmailAssistent />
    : lauf ? <SerienmailDetail key={lauf} laufId={lauf} />
    : <SerienmailListe />;

  const konto = nav?.konten.find(k => k.id === nav.selectedKontoId) ?? nav?.konten[0] ?? null;
  const objekt = nav?.activeTab === 'objekt' ? nav.objekte.find(o => o.id === nav.selectedObjektId) : undefined;
  const titel = !nav ? 'Posteingang' : nav.activeTab === 'objekt' ? (objekt ? objektOrdnerLabel(objekt) : 'Objekt') : ordnerLabel(nav.activeTab);
  const untertitel = konto ? ['Microsoft 365', konto.email_adresse, syncText(konto.letzter_sync, jetzt)].filter(Boolean).join(' · ') : null;

  return (
    <>
      <header className="flex items-end justify-between flex-wrap" style={{ gap: 14 }}>
        <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink3)' }}>E-Mails</span>
          <h1 className="m-0 truncate" style={{ fontSize: mobil ? 30 : 40, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05, maxWidth: '100%' }}>{titel}</h1>
          {untertitel && <span style={{ fontSize: 14, color: 'var(--ink2)' }}>{untertitel}</span>}
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          {mobil && nav && <ImmoButton groesse={44} icon={Tray} onClick={() => setSheet(true)} style={{ boxShadow: 'inset 0 0 0 1px var(--line)' }}>Ordner</ImmoButton>}
          {nav?.onRefresh && <ImmoButton groesse={44} icon={ArrowClockwise} onClick={nav.onRefresh} style={{ boxShadow: 'inset 0 0 0 1px var(--line)' }}>Aktualisieren</ImmoButton>}
          {mobil && nav && <ImmoButton art="akzent" groesse={44} icon={Plus} onClick={nav.onCompose} disabled={!nav.selectedKontoId}>Neue E-Mail</ImmoButton>}
        </div>
      </header>

      <EmailListe userProfile={userProfile} initialEmailId={initialEmailId} onClearInitial={onClearInitial} neu serienmail={serienmail} />

      {sheet && nav && (
        <Sheet titel="Postfach" untertitel={konto ? (konto.anzeigename || konto.bezeichnung || konto.email_adresse) : undefined} onSchliessen={() => setSheet(false)} maxHoehe="80vh">
          <EmailOrdnerNav nav={nav} serienmailErlaubt={serienmailErlaubt} laufend={laufend} mitPostfach={nav.konten.length > 1}
            onGewaehlt={() => setSheet(false)} zeilenHoehe={44} />
        </Sheet>
      )}
    </>
  );
}
